import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import buttonStyles from '../components/Buttons.module.css';
import * as stripeApi from '../api/stripe';
import './Pricing.css';
import { FRONTEND_URL } from '../config';

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleGetPremium = async () => {
    // Check if user is logged in
    if (!token) {
      // Redirect to signup page with message
      navigate('/signup?message=Set up your account now, then continue to secure checkout for full premium access.&redirectTo=/pricing');
      return;
    }

    setLoading(true);
    
    try {
      const { url } = await stripeApi.createCheckoutSession({
        lookup_key: 'premium_monthly',
        success_url: `${FRONTEND_URL}/settings?success=true&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${FRONTEND_URL}/settings?canceled=true`,
      });
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="pricing-container">
      <div className="pricing-content">
        {/* Header */}
        <div className="pricing-header">
          <h1 className="pricing-title">Unlock meals tailored to your dog</h1>
          <p className="pricing-subtitle">
            Get balanced recipes, exact portions, and feeding guidance in seconds
          </p>
        </div>

        {/* Benefits List */}
        <div className="pricing-benefits">
          <div className="pricing-benefit-item">
            <span className="pricing-checkmark">✔</span>
            <span className="pricing-benefit-text">Full recipe library</span>
          </div>
          <div className="pricing-benefit-item">
            <span className="pricing-checkmark">✔</span>
            <span className="pricing-benefit-text">Add and manage dog profiles</span>
          </div>
          <div className="pricing-benefit-item">
            <span className="pricing-checkmark">✔</span>
            <span className="pricing-benefit-text">Unlimited portion calculations</span>
          </div>
          <div className="pricing-benefit-item">
            <span className="pricing-checkmark">✔</span>
            <span className="pricing-benefit-text">Personalized feeding guidance</span>
          </div>
          <div className="pricing-benefit-item">
            <span className="pricing-checkmark">✔</span>
            <span className="pricing-benefit-text">New recipes added regularly</span>
          </div>
          <div className="pricing-benefit-item">
            <span className="pricing-checkmark">✔</span>
            <span className="pricing-benefit-text">Feed your dog with confidence, no guesswork required</span>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="pricing-value-prop">
          <p>Unlock this recipe and the full library</p>
        </div>

        {/* Price */}
        <div className="pricing-price">
          <span className="pricing-amount">$4.99</span>
          <span className="pricing-period">/month</span>
        </div>
        <p className="pricing-cancel">No commitment • Cancel anytime</p>

        {/* CTA Buttons */}
        <div className="pricing-cta-buttons">
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 0.75rem 0', textAlign: 'center' }}>
            Unlock this recipe and all premium features
          </p>
          <button
            className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
            style={{ 
              width: '100%', 
              fontSize: '1.2rem', 
              padding: '1rem 2rem',
              fontWeight: 600,
              marginBottom: '1rem'
            }}
            onClick={handleGetPremium}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Unlock Full Access'}
          </button>
          
          {!token && (
            <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
              <button
                className={buttonStyles.textLinkBtn}
                onClick={() => navigate('/signup')}
                style={{ fontSize: '1rem' }}
              >
                Create Free Account
              </button>
            </div>
          )}
          
          <button
            className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
            style={{ 
              width: '100%', 
              fontSize: '1rem', 
              padding: '0.8rem 1.5rem'
            }}
            onClick={() => navigate(-1)}
          >
            Maybe later
          </button>
        </div>

        {/* Comparison Table */}
        <div className="pricing-comparison">
          <h3 className="pricing-comparison-title">What you get</h3>
          <div className="pricing-comparison-grid">
            {/* Free Column */}
            <div className="pricing-comparison-column">
              <h4 className="pricing-comparison-plan">Free</h4>
              <ul className="pricing-comparison-list">
                <li>2 recipes</li>
                <li>3 portion calculations</li>
              </ul>
            </div>

            {/* Premium Column */}
            <div className="pricing-comparison-column pricing-comparison-column--premium">
              <h4 className="pricing-comparison-plan">Premium</h4>
              <ul className="pricing-comparison-list">
                <li><strong>Unlimited</strong> recipes</li>
                <li><strong>Unlimited</strong> portion calculations</li>
                <li>Save and manage dog profiles</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
